import * as React from 'react';
import { clsx } from 'clsx';
import type { CheckoutStepState } from "../index/utils";

type Props = Readonly<{
	states: CheckoutStepState[],
	// Called with the server step of an already-reached indicator step when it is
	// clicked, to navigate there. Reached-but-not-current steps are the navigable
	// ones — see checkoutStepStates.
	onStepSelect?: (targetStep: number) => void,
}>

function CheckIcon() {
	return (
		<svg className="w-4 h-4 fill-black" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
			<path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/>
		</svg>
	);
}

export default function HorizontalSteps({states, onStepSelect}: Props) {
	return (
		// Rendered as plain elements, not <button>s — a <button> would inherit the
		// global form-button styling (including hover:text-black, which hides the
		// white label against the near-black bar).
		<div className="hidden sm:flex justify-center items-center gap-4 md:gap-8">
			{states.map((step, index) => {
				const navigable = Boolean(onStepSelect) && step.navigable;
				const filled = step.status === 'complete' || step.status === 'current';

				const interaction = navigable ? {
					role: 'button' as const,
					tabIndex: 0,
					onClick: () => onStepSelect!(step.targetStep),
					onKeyDown: (event: React.KeyboardEvent) => {
						if (event.key === 'Enter' || event.key === ' ') {
							event.preventDefault();
							onStepSelect!(step.targetStep);
						}
					},
				} : {};

				return (
					<React.Fragment key={index}>
						<div
							{...interaction}
							className={clsx(
								"flex items-center gap-3 group select-none",
								navigable ? "cursor-pointer" : "cursor-default",
							)}
						>
							<span className={clsx(
								"flex justify-center items-center shrink-0 rounded-full font-medium font-arial w-8 h-8 text-sm",
								filled ? "bg-primary-500 text-black" : "bg-white text-black",
								step.status === 'current' && "ring-2 ring-white ring-offset-2 ring-offset-blackAlmost",
							)}>
								{step.status === 'complete' ? <CheckIcon/> : index + 1}
							</span>

							<span className={clsx(
								"text-sm font-medium text-white",
								navigable && "group-hover:underline underline-offset-4",
								step.status === 'upcoming' && "text-white/60",
							)}>
								{step.label}
							</span>
						</div>

						{index < states.length - 1 &&
							<span className="flex-1 h-0.5 mx-3 min-w-8 bg-white/30"/>
						}
					</React.Fragment>
				);
			})}
		</div>
	);
}
